import _ from 'lodash'
import Vue from 'vue'
import router from '@ps/router'
import { readFile, saveFile } from '@ps/api/editor/file'
import game from '@ps/store/workspace/game'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import store from '@ps/store'
import gameIframe from '@ps/bridge/iframe/iframe'
import pathConfig from '@ps/bridge/path'
import { isVideo } from '@ps/bridge/qc/base'
import {
  getCommonResources,
  getSceneResources,
} from '@ps/bridge/resource/editor-config'
import {
  isGlobalScene,
  isNormalScene,
  isPlayScene,
} from '@ps/bridge/qc/scripts/scene-config'
import { hasSelfDevPermission } from '@ps/permission/workbench'
import iframe from '@ps/bridge/iframe/iframe'
import { CustomEventType } from '@ps/bridge/qc/vp/config'
import { getAnimationName, setAnimationName } from '@ps/workbench/animation'
import {
  gameConfigToi18nStore,
  i18nStoreToGameConfig,
} from '@com/utils/i18n-store-conversion'
import JSONParse from '@/utils/JSONParse'
import * as videoScript from '@ps/bridge/qc/scripts/video'

const editorConfigPath = pathConfig.EDITOR_CONFIG
const tempConfigPath = pathConfig.TEMP_CONFIG

/**
 * playsmart编辑器配置专用参数
 */
export default {
  namespaced: true,

  state: {
    nodeConfig: {
      // uuid: {
      //   original_resource: '', // 原始资源，字符串或对象，如果是序列帧或粒子，是对象；如果是项目相对路径的地址，需要沿用相对路径，因为项目可能会被复制到其他账号中
      //   resource: '' // 被处理过的资源，如图片被裁切后的资源
      //   thumbnail: ''
      // }
    },
    resources: [],
    bg: {
      value: 0,
      // delete: 0 // 兼容基础包，记录是否被手动删除过背景图片，为1则表示被删除过，存在GlobalConfigBg脚本的该值一直未0，不会被更新
    },
    bgm: {
      value: 0,
      // deleted: 0 // 同 bg.delete，记录背景音乐
    },
    // bgmInfo: {
    //   clipStartTime: 0,
    //   clipEndTime: 0,
    //   volume: 100,
    //   duration: 0,
    //   size: 0,
    //   sourceDuration: 0,
    //   sourceSize: 0
    // },
    // 资源名称映射表
    libDataPool: {
      // 资源路径：{ name: string, data: any }
    },
    libClipPool: {
      // path: [clip_path...]
    },
    // 已删除资源列表
    deleteResourcePool: [
      // 资源路径：string
    ],
    storyboard: {
      layout: {},
    },
    i18nStore: [],
  },

  mutations: {
    updateEditorConfig(state, config) {
      Object.assign(state, config)
    },
  },

  actions: {
    async initEditorConfig({ commit, state, dispatch }) {
      const projectKey = router.currentRoute.query.id
      const config = await readFile({
        pid: projectKey,
        path: editorConfigPath,
      })
      const tempConfig = await readFile({
        pid: projectKey,
        path: tempConfigPath,
      })
      if (tempConfig.code === 1) {
        await saveFile(
          {
            pid: projectKey,
            path: tempConfigPath,
          },
          JSON.stringify({}, null, 2),
        )
      }
      if (config.code === 1) {
        await saveFile(
          {
            pid: projectKey,
            path: editorConfigPath,
          },
          JSON.stringify(state, null, 2),
        )
      } else {
        let mergeConfig = config
        if (tempConfig.code === 0) {
          mergeConfig = Object.assign(config, tempConfig)
        }
        commit('updateEditorConfig', mergeConfig)
        if (config.i18nStore) {
          await dispatch('syncEditorI18nStore', {
            i18nStore: config.i18nStore,
          })
        }
        store.commit(
          'workspace/storyboard/initLayoutData',
          config.storyboard ? config.storyboard.layout : {},
        )
      }
    },

    /**
     * 更新 editorConfig
     * @param data {{key: string, value: any} | {key: string, value: any}[]}
     * @returns {Promise<{stage_game_resources: *[], stage_ending_resources: *[]}>}
     */
    async save({ state, commit, dispatch }, data) {
      let config = {}

      // 对象
      if (_.isPlainObject(data)) {
        config = {
          [data.key]: data.value,
        }
      }
      // 数组
      if (_.isArray(data)) {
        _.each(data, (item) => {
          config = {
            ...config,
            [item.key]: item.value,
          }
        })
      }

      const projectKey = router.currentRoute.query.id
      await dispatch('updatePlayableResources')
      await dispatch('updateNodeConfig')
      await dispatch('updateEditorI18nStore')
      if (hasSelfDevPermission()) {
        await dispatch('fixCommonResource')
      }
      const editorConfig = Object.assign(
        {},
        state,
        {
          storyboard: {
            layout: store.state.workspace.storyboard.layout,
          },
        },
        config,
      )

      try {
        // 资源名字长度最大99，因为pt普通换肤里有名字长度校验，要求长度小于100
        const cookResourceName = (data) => {
          for (const i in data) {
            if (data[i].name && data[i].name.length >= 100) {
              data[i].name = data[i].name.slice(0, 99)
            }
            if (data[i].children && data[i].children.length > 0) {
              cookResourceName(data[i].children)
            }
          }
        }
        const cookConfig = (config) => {
          const temp = _.cloneDeep(config)
          // 保存时清空nodeConfig中场景截图数据
          if (temp.nodeConfig) {
            Object.keys(temp.nodeConfig).forEach((key) => {
              if (temp.nodeConfig[key]) {
                temp.nodeConfig[key].thumbnail = ''
              }
            })
          }
          if (temp.resources && temp.resources.length > 0) {
            cookResourceName(temp.resources)
          }
          return _.omit(temp, [
            'stage_ending_resources',
            'stage_game_resources',
          ])
        }
        const res = await saveFile(
          {
            pid: projectKey,
            path: editorConfigPath,
          },
          // 删除 stage_ending_resources、stage_game_resources 历史无用数据
          JSON.stringify(cookConfig(editorConfig), null, 2),
        )
        const tempRes = await saveFile(
          {
            pid: projectKey,
            path: tempConfigPath,
          },
          // 通过临时文件额外保存nodeConfig字段
          JSON.stringify(_.pick(editorConfig, ['nodeConfig']), null, 2),
        )
        if (res.code === 0 || tempRes.code === 0) {
          commit('updateEditorConfig', editorConfig)
          return editorConfig
        }
        return Promise.reject(new Error(res.msg || '更新配置失败'))
      } catch (e) {
        return Promise.reject(new Error(e.message || '更新配置失败'))
      }
    },

    async saveBgConfig({ dispatch }, value) {
      return dispatch('save', {
        key: 'bg',
        value,
      })
    },

    async saveBgmConfig({ state, dispatch }, value) {
      return dispatch('save', {
        key: 'bgm',
        value: _.isPlainObject(value) ? { ...state.bgm, ...value } : value,
      })
    },

    /**
     * 保存资源映射
     */
    async updateNodeConfig({ state, dispatch, commit }) {
      const uuids = (getDescendants(game.state.UIRoot) || []).map(
        (node) => node.uuid,
      )
      uuids.push('background_image')
      uuids.push('background_audio')
      // 过滤多余的配置
      const nodeConfig = _.pick(state.nodeConfig, uuids)
      // 转换资源路径为相对路径
      dispatch('changeResAbsoluteToRelative')
      commit('updateEditorConfig', {
        nodeConfig: nodeConfig,
      })
      return nodeConfig
    },

    /**
     * 保存资源映射
     * https://confluence.mobvista.com/pages/viewpage.action?pageId=58821767
     */
    async updatePlayableResources({ state, commit }) {
      // 只存储普通场景
      const sceneList = gameIframe.gamePlay.children.filter((scene) => {
        return isNormalScene(scene)
      })
      // 玩法场景和全局场景，都放在公共部分
      const commonSceneList = gameIframe.gamePlay.children.filter((scene) => {
        return isGlobalScene(scene) || isPlayScene(scene)
      })
      const defaultLanguage = store.state.workspace.language.defaultLanguage
      const resources = sceneList
        .map((scene) => {
          return getSceneResources({
            scene,
            defaultLanguage,
          })
        })
        .filter(Boolean)

      const commonResources = await getCommonResources({
        bg: state.bg,
        bgm: state.bgm,
        isPickAudioResourceFromAssets:
          !store.state.workspace.project.isNormMaterial,
      })
      const commonSceneResources = commonSceneList
        .map((scene) => {
          return getSceneResources({
            scene,
            defaultLanguage,
          })
        })
        .filter(Boolean)
      commonSceneResources.forEach((item) => {
        commonResources.children = commonResources.children.concat(
          item.children,
        )
      })

      // 获取 gameConfig 赋值到对应场景
      const commonGameConfig = await store.dispatch(
        'workspace/gameConfig/getCommonGameConfig',
        true,
      )

      for (const item of resources) {
        const sceneName = item.name
        const sceneGameConfig = await store.dispatch(
          'workspace/gameConfig/getSceneGameConfig',
          sceneName,
        )
        _.each(sceneGameConfig, (config, key) => {
          item.children.push({
            type: 'gameConfig',
            name: config.title || key,
            path: [key],
          })
        })
      }

      if (!_.isEmpty(commonGameConfig)) {
        _.each(commonGameConfig, (config, key) => {
          commonResources.children.push({
            type: 'gameConfig',
            name: config.title || key,
            path: [key],
          })
        })
      }

      resources.unshift(commonResources)

      const config = {
        resources,
      }
      commit('updateEditorConfig', config)
      return config
    },

    /**
     * 更新节点配置
     * @param node {qc.Node} 节点
     * @param thumbnail {string} 节点截图
     * @param originalResource {string} 原始资源
     * @param resource {string} 资源
     * @param bg {boolean} 是否是背景图片
     */
    updateTargetNodeConfig(
      { state, commit },
      { node, thumbnail, originalResource, resource, bg, bgm },
    ) {
      let uuid = _.get(node, 'uuid')
      // 背景图片使用 background_image 作为假的 uuid
      if (bg) {
        uuid = 'background_image'
      }
      if (bgm) {
        uuid = 'background_audio'
      }
      if (!state.nodeConfig[uuid]) {
        Vue.set(state.nodeConfig, [uuid], {
          original_resource: '',
          resource: '',
          thumbnail: '',
        })
      }
      Vue.set(state.nodeConfig, [uuid], {
        thumbnail:
          thumbnail || _.get(state, `nodeConfig[${uuid}].thumbnail`) || '',
        original_resource:
          originalResource ||
          _.get(state, `nodeConfig[${uuid}].original_resource`) ||
          '',
        resource:
          resource || _.get(state, `nodeConfig[${uuid}].resource`) || '',
      })
    },
    // 资源池图片资源记录资源信息冗余数据，项目初始化阶段协助清理历史数据，一定时间后可以删除
    clearImageLibData({ state, commit }) {
      const pool = state.libDataPool
      Object.keys(pool).forEach((key) => {
        if (key.indexOf('image_') > -1 && _.get(pool[key], 'data')) {
          delete pool[key].data
        }
      })
      commit('updateEditorConfig', {
        libDataPool: pool,
      })
    },
    /**
     * 更新资源别名映射表
     *
     * @param path {string} 资源路径
     * @param data {object} 资源库信息
     *
     **/
    updateLibData({ state, commit }, { path, data }) {
      const pool = state.libDataPool
      pool[path] = data
      commit('updateEditorConfig', {
        libDataPool: pool,
      })
    },
    updateLibClip({ state, commit }, { clipPath, path }) {
      const pool = state.libClipPool
      const paths = Object.keys(pool)
      let tempKey = ''
      paths.forEach((item) => {
        if (item === clipPath || pool[item].some((el) => el === clipPath)) {
          tempKey = item
        }
      })
      if (tempKey) {
        pool[tempKey].push(path)
      } else {
        pool[clipPath] = []
        pool[clipPath].push(path)
      }
      commit('updateEditorConfig', {
        libClipPool: pool,
      })
    },
    /**
     * 移除映射表资源别名
     *
     * @param path {string} 资源路径
     * @param name {string} 资源名称
     *
     **/
    removeLibData({ state, commit }, path) {
      const pool = state.libDataPool
      if (pool[path]) {
        delete pool[path]
      }
      commit('updateEditorConfig', {
        libDataPool: pool,
      })
    },
    // 全量获取当前video节点，更新视频资源映射表数据更新
    updateVideoRefMap({ state, commit }) {
      const allVideoNodes = (getDescendants(game.state.UIRoot) || []).filter(
        (item) => isVideo(item),
      )
      const data = {}
      allVideoNodes.forEach((videoItem) => {
        const videoData = videoScript.getScript(videoItem)
        data[videoData.videoUrl] = videoData.audioUrl
      })
      commit('updateEditorConfig', {
        videoRefMap: data,
      })
    },
    /**
     * 删除资源池添加路径
     */
    addDeleteResourcePool({ state, commit }, { path }) {
      const pool = state.deleteResourcePool
      if (pool.includes(path)) return
      pool.push(path)
      commit('updateEditorConfig', {
        deleteResourcePool: pool,
      })
    },
    /**
     * 删除资源池移除路径
     */
    removeDeleteResourcePool({ state, commit }, { path }) {
      const pool = state.deleteResourcePool
      commit('updateEditorConfig', {
        deleteResourcePool: pool.filter((item) => item !== path),
      })
    },
    // 自研素材提取common资源
    async fixCommonResource({ state }) {
      const commonIndex = state.resources.findIndex((item) => {
        return item.name === 'common' && item.type === 'common'
      })
      const pathList = []
      state.resources.forEach((item) => {
        if (item.name !== 'common' && item.type !== 'common') {
          for (let i = 0; i < item.children.length; i++) {
            const child = item.children[i]
            if (child.path && /resource\/common\//.test(child.path[0])) {
              const commonRes = item.children.splice(i--, 1)
              if (!pathList.includes(child.path[0])) {
                state.resources[commonIndex].children.push(commonRes[0])
                pathList.push(child.path[0])
              }
            } else if (child.type === 'group') {
              for (let j = 0; j < child.children.length; j++) {
                const _child = child.children[j]
                if (_child.path && /^resource\/common\//.test(_child.path[0])) {
                  const _commonRes = child.children.splice(j--, 1)
                  if (!pathList.includes(_child.path[0])) {
                    state.resources[commonIndex].children.push(_commonRes[0])
                    pathList.push(_commonRes[0].path[0])
                  }
                }
              }
            }
          }
        }
      })
    },
    // 兼容历史素材，如果存在绝对路径，转换为相对路径
    changeResAbsoluteToRelative({ state }) {
      _.each(state.nodeConfig, (item) => {
        if (item.resource && item.resource.includes(iframe.projectPath)) {
          item.resource = item.resource.replace(`${iframe.projectPath}/`, '')
        }
        if (
          item.original_resource &&
          item.original_resource.includes(iframe.projectPath)
        ) {
          item.original_resource = item.original_resource.replace(
            `${iframe.projectPath}/`,
            '',
          )
        }
      })
    },

    updateEditorI18nStore({ state, commit }) {
      // 格式必须要是 { type: string, id: string, label: string, labelEn: string }
      let i18nStore = []
      if (store.state.workspace.gameConfig.gameConfig) {
        const gameConfig = store.state.workspace.gameConfig.gameConfig
        const gameConfigI18nConfig = {}
        // playConfig 为空根据 gameConfig 生成
        const rec = (data, path, cb) => {
          _.each(data, (item, key) => {
            cb(item, [...path, key])
            if (item.items) {
              rec(item.items, [...path, key, 'items'], cb)
            }
          })
        }
        rec(gameConfig.gameConfig, [], (data, paths) => {
          const path = paths.map((item) => `[${item}]`).join('')
          gameConfigI18nConfig[path] = {
            ..._.pick(data, [
              'name',
              'name_en',
              'desc',
              'desc_en',
              'options',
              'options_en',
              'unit_en',
              'unit',
              'title',
              'title_en',
            ]),
          }
        })
        const i18nStoreArray = gameConfigToi18nStore(gameConfigI18nConfig)
        i18nStore = i18nStore.concat(i18nStoreArray)
      }
      const animationBuiltinIds = []

      function isEmpty(obj) {
        return Object.keys(obj).length === 0
      }

      function setCustomEventName(data, nodeUUID) {
        if (data.vpAst && !isEmpty(data.vpAst)) {
          for (const key in data.vpAst) {
            // 过滤自定义事件数据
            if (
              data.vpAst[key].condition &&
              data.vpAst[key].condition.length > 0 &&
              data.vpAst[key].condition[0].content ===
                CustomEventType.CUSTOM_EVENT
            ) {
              i18nStore.push({
                type: 'customEvent',
                id: `[${nodeUUID}][${data.vpAst[key].id}]`,
                label: data.vpAst[key].eventName,
                labelEn:
                  data.vpAst[key].eventNameEn || data.vpAst[key].eventName,
              })
            }
          }
        }
      }

      function setTweenName(scriptData, nodeUUID) {
        if (scriptData.animationName) {
          if (scriptData.builtinId) {
            const tempId = `[${nodeUUID}][${scriptData.builtinId}]`
            // 如果已经录入了这个节点动画组的数据，就不再录入
            if (animationBuiltinIds.includes(tempId)) {
              return
            }
            animationBuiltinIds.push(tempId)
          }
          i18nStore.push({
            type: 'tween',
            id: `[${nodeUUID}][${scriptData.builtinId || scriptData.uuid}]`,
            label: getAnimationName(scriptData.animationName, 'zh-cn'),
            labelEn: getAnimationName(scriptData.animationName, 'en'),
          })
        }
      }

      function setDrawLineName(scriptData) {
        if (
          scriptData &&
          scriptData.$data &&
          scriptData.$data.drawLineData &&
          JSONParse(scriptData.$data.drawLineData)
        ) {
          const drawLineData = JSONParse(scriptData.$data.drawLineData)
          drawLineData.forEach((item) => {
            i18nStore.push({
              type: 'drawLine',
              id: item.uuid,
              label: item.name,
              labelEn: item.nameEn,
            })
          })
        }
      }

      function loopFn(node) {
        if (node.children && node.children.length) {
          node.children.forEach(loopFn)
        }
        if (node.data) {
          setCustomEventName(node.data, node.uuid)
          setDrawLineName(node.data)
        }
        if (
          node.scripts &&
          node.scripts.length &&
          node.scripts.some((script) => script.class.indexOf('qc.Tween') > -1)
        ) {
          node.scripts
            .filter((script) => script.class.indexOf('qc.Tween') > -1)
            .forEach((script) => {
              setTweenName(script, node.uuid)
            })
        }
      }

      loopFn(gameIframe.gamePlay)

      commit('updateEditorConfig', {
        i18nStore,
      })
    },

    // 同步最新的 i18nStore 到 gamePlay 中
    syncEditorI18nStore({ state, commit, dispatch }, { i18nStore }) {
      function setTweenName(scriptData, nodeUUID) {
        if (scriptData.animationName) {
          // 如果是动画组合
          if (scriptData.builtinId) {
            const targetItem = i18nStore.find((item) => {
              return item.id === `[${nodeUUID}][${scriptData.builtinId}]`
            })
            if (!targetItem) {
              console.warn('找不到对应的动画组合数据', scriptData.builtinId)
              return
            }
            scriptData.animationName = setAnimationName({
              name: targetItem.label,
              name_en: targetItem.labelEn,
            })
          } else {
            const targetItem = i18nStore.find((item) => {
              return item.id === `[${nodeUUID}][${scriptData.uuid}]`
            })
            if (!targetItem) {
              console.warn('找不到对应的动画数据', scriptData.uuid)
              return
            }
            scriptData.animationName = setAnimationName({
              name: targetItem.label,
              name_en: targetItem.labelEn,
            })
          }
        }
      }

      function setCustomEventName(data, nodeUUID) {
        if (data.vpAst && Object.keys(data.vpAst).length) {
          for (const key in data.vpAst) {
            // 过滤自定义事件数据
            if (
              data.vpAst[key].condition &&
              data.vpAst[key].condition.length > 0 &&
              data.vpAst[key].condition[0].content ===
                CustomEventType.CUSTOM_EVENT
            ) {
              const targetItem = i18nStore.find((item) => {
                return item.id === `[${nodeUUID}][${data.vpAst[key].id}]`
              })
              if (!targetItem) {
                console.warn('找不到对应的自定义事件数据', data.vpAst[key].id)
                return
              }
              data.vpAst[key].eventName = targetItem.label
              data.vpAst[key].eventNameEn = targetItem.labelEn
            }
          }
        }
      }

      function setDrawLineName(scriptData) {
        if (
          scriptData &&
          scriptData.$data &&
          scriptData.$data.drawLineData &&
          JSONParse(scriptData.$data.drawLineData)
        ) {
          const drawLineData = JSONParse(scriptData.$data.drawLineData)
          drawLineData.forEach((item) => {
            const targetItem = i18nStore.find((i) => i.id === item.uuid)
            if (!targetItem) {
              console.warn('找不到对应的连线数据', item.id)
              return
            }
            item.name = targetItem.label
            item.nameEn = targetItem.labelEn
          })
          scriptData.$data.drawLineData = JSON.stringify(drawLineData)
        }
      }

      function loopFn(node) {
        if (node.children && node.children.length) {
          node.children.forEach(loopFn)
        }
        if (node.data) {
          setCustomEventName(node.data, node.uuid)
          setDrawLineName(node.data)
        }
        if (
          node.scripts &&
          node.scripts.length &&
          node.scripts.some((script) => script.class.indexOf('qc.Tween') > -1)
        ) {
          node.scripts
            .filter((script) => script.class.indexOf('qc.Tween') > -1)
            .forEach((script) => {
              setTweenName(script, node.uuid)
            })
        }
      }

      if (
        gameIframe &&
        gameIframe.iframe &&
        gameIframe.UIRoot &&
        gameIframe.gamePlay
      ) {
        const gameConfigStore = i18nStore.filter((item) => {
          return (
            item.type !== 'customEvent' &&
            item.type !== 'tween' &&
            item.type !== 'drawLine'
          )
        })
        const gameConfigI18nConfig = i18nStoreToGameConfig(gameConfigStore)
        // 更新最新的 gameConfig playConfig 数据
        store.commit(
          'workspace/gameConfig/updateI18nConfig',
          gameConfigI18nConfig,
        )
        loopFn(gameIframe.gamePlay)
      } else {
        setTimeout(() => {
          dispatch('syncEditorI18nStore', { i18nStore })
        }, 1000)
      }
    },
  },
}
